// 20.02.26 - Admin page: suspend and restore user accounts with audit log
import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

const ADMIN_EMAIL = 'tom@cooc.com.au';

const SUSPENSION_REASONS = [
  'Non-payment',
  'User request',
  'Behaviour concern',
  'Account under review'
];

const AdminPage = () => {
  const { currentUser } = useAuth();
  const [coocEmail, setCoocEmail] = useState('');
  const [reason, setReason] = useState('Non-payment');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [activeTab, setActiveTab] = useState('suspend'); // suspend | restore | log

  // 20.02.26 - Block non-admins
  if (currentUser?.email !== ADMIN_EMAIL) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2 style={{ color: '#dc2626' }}>Access Denied</h2>
        <p>This page is for administrators only.</p>
      </div>
    );
  }

  // 20.02.26 - Load audit log
  const loadAuditLog = async () => {
    try {
      const db = getFirestore();
      const q = query(
        collection(db, 'AuditLog'),
        orderBy('timestamp', 'desc'),
        limit(50)
      );
      const snapshot = await getDocs(q);
      setAuditLog(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      console.error('20.02.26 - Audit log load error:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'log') loadAuditLog();
  }, [activeTab]);

  const handleSuspend = async () => {
    if (!coocEmail.includes('@cooc.com.au')) {
      setStatus('❌ Must be a @cooc.com.au email address');
      return;
    }
    setLoading(true);
    setStatus('⏳ Suspending account...');
    try {
      const fns = getFunctions();
      const suspendUser = httpsCallable(fns, 'suspendUser');
      const result = await suspendUser({ coocEmail, reason, notes });
      setStatus(`✅ ${result.data.message}`);
      setCoocEmail('');
      setNotes('');
    } catch (err) {
      setStatus(`❌ Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!coocEmail.includes('@cooc.com.au')) {
      setStatus('❌ Must be a @cooc.com.au email address');
      return;
    }
    setLoading(true);
    setStatus('⏳ Restoring account...');
    try {
      const fns = getFunctions();
      const restoreUser = httpsCallable(fns, 'restoreUser');
      const result = await restoreUser({ coocEmail, notes });
      setStatus(`✅ ${result.data.message}`);
      setCoocEmail('');
      setNotes('');
    } catch (err) {
      setStatus(`❌ Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-AU', { 
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const tabStyle = (tab) => ({
    padding: '10px 24px',
    border: 'none',
    borderBottom: activeTab === tab ? '3px solid #0891b2' : '3px solid transparent',
    background: 'none',
    fontWeight: activeTab === tab ? '700' : '500',
    color: activeTab === tab ? '#0891b2' : '#64748b',
    cursor: 'pointer',
    fontSize: '15px'
  });

  return (
    <div style={{ maxWidth: '680px', margin: '40px auto', padding: '0 16px' }}>
      <h1 style={{ color: '#1e293b', marginBottom: '4px' }}>COOC Admin</h1>
      <p style={{ color: '#64748b', marginBottom: '32px', fontSize: '14px' }}>
        Account management — all actions are logged to the audit trail
      </p>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid #e2e8f0', marginBottom: '32px', display: 'flex' }}>
        <button style={tabStyle('suspend')} onClick={() => { setActiveTab('suspend'); setStatus(''); }}>
          ⏸ Suspend
        </button>
        <button style={tabStyle('restore')} onClick={() => { setActiveTab('restore'); setStatus(''); }}>
          ▶️ Restore
        </button>
        <button style={tabStyle('log')} onClick={() => setActiveTab('log')}>
          📋 Audit Log
        </button>
      </div>

      {/* Suspend / Restore form */}
      {(activeTab === 'suspend' || activeTab === 'restore') && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '32px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          
          <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#1e293b' }}>
            User's COOC Email
          </label>
          <input
            type="email"
            value={coocEmail}
            onChange={(e) => setCoocEmail(e.target.value)}
            placeholder="jane.doe@cooc.com.au"
            style={{ width: '100%', padding: '12px', fontSize: '15px',
              border: '1px solid #cbd5e1', borderRadius: '8px',
              boxSizing: 'border-box', marginBottom: '20px' }}
          />

          {/* Reason — only for suspend */}
          {activeTab === 'suspend' && (
            <>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#1e293b' }}>
                Reason
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                style={{ width: '100%', padding: '12px', fontSize: '15px',
                  border: '1px solid #cbd5e1', borderRadius: '8px',
                  boxSizing: 'border-box', marginBottom: '20px' }}
              >
                {SUSPENSION_REASONS.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </>
          )}

          <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#1e293b' }}>
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional notes for the audit log..."
            rows={3}
            style={{ width: '100%', padding: '12px', fontSize: '15px',
              border: '1px solid #cbd5e1', borderRadius: '8px',
              boxSizing: 'border-box', marginBottom: '24px', resize: 'vertical' }}
          />

          <button
            onClick={activeTab === 'suspend' ? handleSuspend : handleRestore}
            disabled={loading}
            style={{
              width: '100%', padding: '14px', fontSize: '16px', fontWeight: '600',
              background: activeTab === 'suspend' ? '#dc2626' : '#16a34a',
              color: 'white', border: 'none', borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Processing...' : activeTab === 'suspend' ? '⏸ Suspend Account' : '▶️ Restore Account'}
          </button>

          {status && (
            <div style={{ marginTop: '20px', padding: '16px', background: '#f8fafc',
              borderRadius: '8px', fontSize: '14px', color: '#1e293b' }}>
              {status}
            </div>
          )}
        </div>
      )}

      {/* Audit Log */}
      {activeTab === 'log' && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>Recent Actions</h2>
            <button onClick={loadAuditLog} style={{ background: 'none', border: '1px solid #cbd5e1',
              borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '13px' }}>
              Refresh
            </button>
          </div>
          {auditLog.length === 0 ? (
            <p style={{ color: '#94a3b8', textAlign: 'center', padding: '24px' }}>No audit records yet</p>
          ) : (
            auditLog.map(entry => (
              <div key={entry.id} style={{ padding: '16px 0', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>{entry.coocEmail}</span>
                  <span style={{ fontSize: '12px', color: '#94a3b8' }}>{formatDate(entry.timestamp)}</span>
                </div>
                <div style={{ fontSize: '13px', color: '#64748b' }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 10px', borderRadius: '20px',
                    background: entry.action === 'suspended' ? '#fef2f2' : '#f0fdf4',
                    color: entry.action === 'suspended' ? '#dc2626' : '#16a34a',
                    fontWeight: '600', marginRight: '8px', fontSize: '12px'
                  }}>
                    {entry.action.toUpperCase()}
                  </span>
                  {entry.reason && <span>Reason: {entry.reason}</span>}
                  {entry.notes && <span style={{ marginLeft: '8px' }}>— {entry.notes}</span>}
                </div>
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                  By: {entry.performedBy}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPage;